import { useEffect, useRef } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Header from './components/common/Header'
import Footer from './components/common/Footer'
import MobileNavigation from './components/common/MobileNavigation'
import DemoModal from './components/common/DemoModal'
import ContactModal from './components/common/ContactModal'
import SupportModal from './components/common/SupportModal'
import PrivacyPolicyModal from './components/common/PrivacyPolicyModal'
import PlatformPrivacyPolicyModal from './components/common/PlatformPrivacyPolicyModal'
import TermsOfServiceModal from './components/common/TermsOfServiceModal'
import DataResidencyModal from './components/common/DataResidencyModal'
import AccessibilityModal from './components/common/AccessibilityModal'
import PartnershipModal from './components/common/PartnershipModal'
import SkipLinks from './components/common/SkipLinks'
import ScrollToTop from './components/common/ScrollToTop'
import CookieBanner from './components/common/CookieBanner'
import CookiePreferences from './components/common/CookiePreferences'
import CookiePolicyModal from './components/common/CookiePolicyModal'

// Pages
import Home from './pages/Home'
import About from './pages/About'
import Customers from './pages/Customers'
import Technical from './pages/Technical'
import ServiceSummary from './pages/ServiceSummary'
import AI from './pages/AI'
import ROI from './pages/ROI'
import UseCase from './pages/UseCase/UseCase'
import UseCaseDetail from './pages/UseCase/UseCaseDetail'
import WhitePapers from './pages/WhitePaper/WhitePapers'
import WhitePaperDetail from './pages/WhitePaper/WhitePaperDetail'
import AiInHealthcareOperations from './pages/WhitePaper/AiInHealthcareOperations'
import SchedulingCXWhitePaper from './pages/WhitePaper/SchedulingCX'
import ReferralCXWhitePaper from './pages/WhitePaper/ReferralCX'
import ProbationCXWhitePaper from './pages/WhitePaper/ProbationCX'
import IntegratedCareWhitePaper from './pages/WhitePaper/IntegratedCare'
import CellCXWhitePaper from './pages/WhitePaper/CellCX'
import Webinars from './pages/Webinars'
import Blog from './pages/Blog'
import News from './pages/News'
import Accessibility from './pages/Accessibility'

// Product pages
import ReferralCX from './pages/products/ReferralCX'
import SchedulingCX from './pages/products/SchedulingCX'
import EConsultCX from './pages/products/EConsultCX'
import CaseCX from './pages/products/CaseCX'
import ConsumerCX from './pages/products/ConsumerCX'
import VaxManagerCX from './pages/products/VaxManagerCX'
import CellCX from './pages/products/CellCX'
import ProbationCX from './pages/products/ProbationCX'

// Store
import { useUserStore } from './stores/user'
import { useNavigationStore } from './stores/navigation'
import { useCookieConsentStore } from './stores/cookieConsent'

function App() {
  const location = useLocation()
  const userStore = useUserStore()
  const navigationStore = useNavigationStore()
  const cookieConsentStore = useCookieConsentStore()
  const hasInitializedBanner = useRef(false)

  // Update document title and meta description on route change
  useEffect(() => {
    const updateMeta = () => {
      const path = location.pathname
      let title = 'EceptionistCX - Smarter Collaboration. Better Care.'
      let description = 'Streamline healthcare workflows with EceptionistCX intelligent collaboration platform'

      // Route-specific meta data
      const routeMeta: Record<string, { title: string; description: string }> = {
        '/': {
          title: 'Home - EceptionistCX Healthcare Platform',
          description: 'Streamline healthcare workflows with EceptionistCX intelligent collaboration platform'
        },
        '/about': {
          title: 'About - EceptionistCX',
          description: 'Learn about Eceptionist\'s 20+ year history in healthcare technology innovation'
        },
        '/who-we-serve': {
          title: 'Who We Serve - EceptionistCX',
          description: 'Healthcare organizations worldwide trust EceptionistCX to improve efficiency and patient care'
        },
        '/technical': {
          title: 'Technical Overview - EceptionistCX',
          description: 'Explore our platform architecture, integration capabilities, and security features'
        },
        '/service-summary': {
          title: 'Service Summary - EceptionistCX',
          description: 'Comprehensive overview of all EceptionistCX platform services and capabilities'
        },
        '/ai': {
          title: 'AI in Healthcare - EceptionistCX',
          description: 'Discover how AI enhances healthcare efficiency and care delivery'
        },
        '/roi-calculator': {
          title: 'ROI Calculator - EceptionistCX',
          description: 'Calculate your return on investment with EceptionistCX solutions'
        },
        '/use-case-finder': {
          title: 'Use Case Finder - EceptionistCX',
          description: 'Find how EceptionistCX can solve your specific healthcare challenges'
        },
        '/use-case-detail': {
          title: 'Case Study Detail - EceptionistCX',
          description: 'Detailed case study showing real-world EceptionistCX implementation results'
        },
        '/whitepapers': {
          title: 'White Papers - EceptionistCX Resources',
          description: 'Access healthcare technology insights and research papers'
        },
        '/webinars': {
          title: 'Webinars - EceptionistCX',
          description: 'Join our healthcare technology webinars and educational events'
        },
        '/blog': {
          title: 'Blog - EceptionistCX',
          description: 'Latest insights and updates from the healthcare technology industry'
        },
        '/news': {
          title: 'News - EceptionistCX',
          description: 'Company news and announcements from EceptionistCX'
        }
      }

      if (routeMeta[path]) {
        title = routeMeta[path].title
        description = routeMeta[path].description
      }

      document.title = title
      
      const descriptionMeta = document.querySelector('meta[name="description"]')
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', description)
      }
    }

    updateMeta()
  }, [location])

  // Load user preferences on app start
  useEffect(() => {
    userStore.loadPreferences()
  }, [userStore])

  // Show cookie banner on first visit
  useEffect(() => {
    if (!hasInitializedBanner.current && !cookieConsentStore.hasConsented) {
      hasInitializedBanner.current = true
      cookieConsentStore.showBannerAction()
    }
  }, [])

  return (
    <div className="min-h-screen bg-background-light">
      {/* Skip Links for Accessibility */}
      <SkipLinks />
      
      {/* Scroll to top on route change */}
      <ScrollToTop />
      
      {/* Header */}
      <Header />
      
      {/* Mobile Navigation Drawer */}
      {navigationStore.isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => navigationStore.closeMobileMenu()}
          />
          <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl">
            <MobileNavigation />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main id="main-content" className="main-content" role="main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/who-we-serve" element={<Customers />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/technical" element={<Technical />} />
          <Route path="/service-summary" element={<ServiceSummary />} />
          <Route path="/ai" element={<AI />} />
          <Route path="/roi-calculator" element={<ROI />} />
          <Route path="/use-case-finder" element={<UseCase />} />
          <Route path="/use-case-detail/:slug" element={<UseCaseDetail />} />
          <Route path="/whitepapers" element={<WhitePapers />} />
          <Route path="/whitepapers/:id" element={<WhitePaperDetail />} />
          
          {/* Specific white paper routes */}
          <Route path="/whitepapers/ai-in-healthcare-operations" element={<AiInHealthcareOperations />} />
          <Route path="/whitepapers/scheduling-cx" element={<SchedulingCXWhitePaper />} />
          <Route path="/whitepapers/referral-cx" element={<ReferralCXWhitePaper />} />
          <Route path="/whitepapers/probation-cx" element={<ProbationCXWhitePaper />} />
          <Route path="/whitepapers/integrated-care" element={<IntegratedCareWhitePaper />} />
          <Route path="/whitepapers/cell-cx" element={<CellCXWhitePaper />} />
          <Route path="/webinars" element={<Webinars />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/news" element={<News />} />
          <Route path="/accessibility" element={<Accessibility />} />
          
          {/* Product routes */}
          <Route path="/products/referralcx" element={<ReferralCX />} />
          <Route path="/products/schedulingcx" element={<SchedulingCX />} />
          <Route path="/products/econsultcx" element={<EConsultCX />} />
          <Route path="/products/casecx" element={<CaseCX />} />
          <Route path="/products/consumercx" element={<ConsumerCX />} />
          <Route path="/products/vaxmanagercx" element={<VaxManagerCX />} />
          <Route path="/products/cellcx" element={<CellCX />} />
          <Route path="/products/probationcx" element={<ProbationCX />} />
          
          {/* Redirects */}
          <Route path="/products" element={<Home />} />
          <Route path="/resources" element={<WhitePapers />} />
        </Routes>
      </main>

      {/* Footer */}
      <Footer />

      {/* Modals */}
      <DemoModal 
        isOpen={navigationStore.isDemoModalOpen} 
        onClose={navigationStore.closeDemoModal} 
      />
      <ContactModal 
        isOpen={navigationStore.isContactModalOpen} 
        onClose={navigationStore.closeContactModal} 
      />
      <SupportModal 
        isOpen={navigationStore.isSupportModalOpen} 
        onClose={navigationStore.closeSupportModal} 
      />
      <PrivacyPolicyModal 
        isOpen={navigationStore.isPrivacyPolicyModalOpen} 
        onClose={navigationStore.closePrivacyPolicyModal} 
      />
      <PlatformPrivacyPolicyModal 
        isOpen={navigationStore.isPlatformPrivacyPolicyModalOpen} 
        onClose={navigationStore.closePlatformPrivacyPolicyModal} 
      />
      <TermsOfServiceModal 
        isOpen={navigationStore.isTermsOfServiceModalOpen} 
        onClose={navigationStore.closeTermsOfServiceModal} 
      />
      <DataResidencyModal 
        isOpen={navigationStore.isDataResidencyModalOpen} 
        onClose={navigationStore.closeDataResidencyModal} 
      />
      <AccessibilityModal 
        isOpen={navigationStore.isAccessibilityModalOpen} 
        onClose={navigationStore.closeAccessibilityModal} 
      />
      <PartnershipModal 
        isOpen={navigationStore.isPartnershipModalOpen} 
        onClose={navigationStore.closePartnershipModal} 
      />

      {/* Cookie Components */}
      <CookieBanner />
      <CookiePreferences />
      <CookiePolicyModal 
        isOpen={cookieConsentStore.showCookiePolicy} 
        onClose={cookieConsentStore.hideCookiePolicyAction} 
      />
    </div>
  )
}

export default App

